// Lip-sync and life for a talking presenter. Pure functions, no drawing: they turn the narration's real sound into how open the mouth is and what
// shape it takes at every moment, and give the blinking and the small head movements of a person who is speaking.
//
// The mouth follows the SOUND, not a guess: the narration is cut into 20 ms frames, and for each one the loudness and where the energy sits
// (low "oo/mm", middle "aa/oh", high "ss/ee/f") say how wide the jaw opens and whether the lips round or spread.

export const FPS = 50;
const clamp = (x, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, x));
const smoothstep = (x) => { const t = clamp(x); return t * t * (3 - 2 * t); };

// Smoothing with different speeds up and down: a mouth opens quickly and closes a little more slowly.
function follow(values, attack, release) {
  const dt = 1 / FPS;
  const up = 1 - Math.exp(-dt / attack);
  const down = 1 - Math.exp(-dt / release);
  const out = new Float32Array(values.length);
  let current = 0;
  for (let i = 0; i < values.length; i += 1) {
    current += (values[i] - current) * (values[i] > current ? up : down);
    out[i] = current;
  }
  return out;
}

const percentile = (list, p) => {
  if (!list.length) return 0;
  const sorted = Float32Array.from(list).sort();
  return sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * p))];
};

// buffer: an AudioBuffer (or anything with sampleRate, length and getChannelData). Returns per-frame curves, all 0..1:
//   open  - how far the jaw drops          wide  - lips spread, teeth show (ee, ss, f)
//   round - lips pushed forward (oo, w)    energy / nod - how loud the speech is and how fast it just got louder (for brows and head nods)
export function analyseSpeech(buffer) {
  const rate = buffer.sampleRate;
  const samples = buffer.getChannelData(0);
  const hop = Math.max(1, Math.round(rate / FPS));
  const frames = Math.max(1, Math.ceil(samples.length / hop));
  const a1 = 1 - Math.exp((-2 * Math.PI * 600) / rate);
  const a2 = 1 - Math.exp((-2 * Math.PI * 3000) / rate);
  const eLow = new Float32Array(frames);
  const eMid = new Float32Array(frames);
  const eHigh = new Float32Array(frames);
  const level = new Float32Array(frames);
  let lp1 = 0;
  let lp2 = 0;
  for (let f = 0; f < frames; f += 1) {
    const from = f * hop;
    const to = Math.min(samples.length, from + hop);
    let sum = 0;
    let low = 0;
    let mid = 0;
    let high = 0;
    for (let i = from; i < to; i += 1) {
      const x = samples[i];
      lp1 += a1 * (x - lp1);
      lp2 += a2 * (x - lp2);
      const l = lp1;
      const m = lp2 - lp1;
      const h = x - lp2;
      sum += x * x;
      low += l * l;
      mid += m * m;
      high += h * h;
    }
    const n = Math.max(1, to - from);
    level[f] = Math.sqrt(sum / n);
    eLow[f] = low / n;
    eMid[f] = mid / n;
    eHigh[f] = high / n;
  }

  // loud = the level that speech reaches (not the single loudest click): the 90th percentile of the frames that are clearly not silence
  const floor = percentile(level, 0.1) * 1.5 + 1e-4;
  const voicedLevels = Array.from(level).filter((v) => v > floor * 2);
  const reference = Math.max(percentile(voicedLevels, 0.9), floor * 3, 1e-3);

  const open = new Float32Array(frames);
  const wide = new Float32Array(frames);
  const round = new Float32Array(frames);
  const energy = new Float32Array(frames);
  for (let f = 0; f < frames; f += 1) {
    const loud = clamp(level[f] / reference, 0, 1.2);
    const voiced = smoothstep((level[f] - floor) / (floor * 2.5 + 1e-5));
    const total = eLow[f] + eMid[f] + eHigh[f] + 1e-12;
    const rLow = eLow[f] / total;
    const rMid = eMid[f] / total;
    const rHigh = eHigh[f] / total;
    const strength = voiced * Math.min(1, loud);
    // "aa/oh" (energy in the middle) opens wide; "oo/mm" (all low) opens little and rounds; "ss/f" (high) opens little and spreads
    const vowel = clamp(0.4 + rMid * 1.5 - rHigh * 0.7, 0.25, 1);
    open[f] = clamp(Math.pow(strength, 0.65) * vowel * 1.08);
    round[f] = clamp((rLow - 0.62) * 3.2) * strength;
    wide[f] = clamp((rHigh - 0.12) * 4) * Math.min(1, strength * 1.6);
    energy[f] = strength;
  }
  const slow = follow(energy, 0.12, 0.2);
  const nod = new Float32Array(frames);
  const lag = Math.round(0.22 * FPS);
  for (let f = 0; f < frames; f += 1) nod[f] = clamp((slow[f] - (slow[Math.max(0, f - lag)] || 0)) * 3.2);
  return { fps: FPS, frames, seconds: samples.length / rate, open: follow(open, 0.04, 0.08), wide: follow(wide, 0.05, 0.1), round: follow(round, 0.06, 0.11), energy: slow, nod: follow(nod, 0.06, 0.2) };
}

const at = (curve, t) => {
  const position = t * FPS;
  const i = Math.floor(position);
  if (i < 0 || i >= curve.length) return 0;
  const next = curve[Math.min(curve.length - 1, i + 1)];
  return curve[i] + (next - curve[i]) * (position - i);
};

// The mouth at time t (seconds from the start of the narration). Silent before and after the narration.
export function mouthAt(analysis, t) {
  if (!analysis || t < 0 || t > analysis.seconds + 0.2) return { open: 0, wide: 0, round: 0, energy: 0, nod: 0 };
  return { open: at(analysis.open, t), wide: at(analysis.wide, t), round: at(analysis.round, t), energy: at(analysis.energy, t), nod: at(analysis.nod, t) };
}

// ------------------------------------------------------------------------------------------ the rest of a living face
function mulberry(seed) {
  let a = (seed >>> 0) || 1;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// When a person blinks over `seconds`: every 2 to 5 seconds, sometimes twice in a row. [{ at, length }] - the same list for the same seed, so
// the preview and the saved video blink together.
export function blinkSchedule(seed = 1, seconds = 600) {
  const random = mulberry(seed * 7919 + 13);
  const blinks = [];
  let t = 0.9 + random() * 1.5;
  while (t < seconds) {
    blinks.push({ at: t, length: 0.15 + random() * 0.05 });
    if (random() < 0.14) blinks.push({ at: t + 0.34, length: 0.15 });
    t += 2.1 + random() * 3.1;
  }
  return blinks;
}

// How closed the eyes are at time t: 0 = open, 1 = shut. The lids close fast and open a little slower.
export function blinkAt(schedule, t) {
  for (let i = 0; i < schedule.length; i += 1) {
    const b = schedule[i];
    if (t < b.at) return 0;
    const x = (t - b.at) / b.length;
    if (x <= 1) return x < 0.4 ? smoothstep(x / 0.4) : 1 - smoothstep((x - 0.4) / 0.6);
  }
  return 0;
}

// The small movements of the head and body of someone who is talking, in units of the head's width. `speech` is the mouth state (for nods).
export function headPose(t, speech = { energy: 0, nod: 0 }, seed = 1) {
  const p = seed * 1.7;
  const sway = Math.sin(t * 0.62 + p) * 0.6 + Math.sin(t * 1.17 + p * 2.1) * 0.4;
  const tilt = Math.sin(t * 0.47 + p * 0.6) * 0.65 + Math.sin(t * 0.93 + p) * 0.35;
  const nod = speech.nod || 0;
  const talk = speech.energy || 0;
  return {
    dx: sway * 0.012,
    dy: Math.sin(t * 0.8 + p) * 0.006 + nod * 0.028 + talk * Math.sin(t * 5.1) * 0.003,
    rot: (tilt * 0.9 + (talk > 0.15 ? Math.sin(t * 1.9 + p) * 0.9 * talk : 0)) * (Math.PI / 180),
    breathe: 1 + Math.sin(t * 1.55 + p) * 0.0035,
    brow: clamp(nod * 0.9 + talk * 0.25),
    gazeX: Math.sin(t * 0.35 + p) * 0.5 + Math.sin(t * 0.9 + p * 3) * 0.3,
    gazeY: Math.sin(t * 0.41 + p * 2) * 0.25,
  };
}

// A made-up stretch of speech, for previews before any narration exists: syllables of varying size with small pauses between words and sentences.
// Same shape as analyseSpeech()'s result, so the studio draws it exactly like real speech.
export function demoSpeech(seconds = 6) {
  const frames = Math.round(seconds * FPS);
  const random = mulberry(99);
  const open = new Float32Array(frames);
  const wide = new Float32Array(frames);
  const round = new Float32Array(frames);
  const energy = new Float32Array(frames);
  const nod = new Float32Array(frames);
  let f = Math.round(0.3 * FPS);
  while (f < frames) {
    const syllable = Math.round((0.12 + random() * 0.1) * FPS);
    const size = 0.35 + random() * 0.65;
    const kind = random();
    for (let i = 0; i < syllable && f + i < frames; i += 1) {
      const bump = Math.sin((Math.PI * (i + 0.5)) / syllable);
      open[f + i] = size * bump;
      energy[f + i] = size * bump;
      if (kind > 0.8) round[f + i] = 0.9 * bump;
      else if (kind < 0.22) wide[f + i] = 0.9 * bump;
    }
    f += syllable;
    const gap = random();
    f += Math.round((gap > 0.88 ? 0.5 : gap > 0.6 ? 0.14 : 0.02) * FPS);
  }
  return { fps: FPS, frames, seconds, open: follow(open, 0.03, 0.05), wide: follow(wide, 0.04, 0.07), round: follow(round, 0.04, 0.08), energy: follow(energy, 0.1, 0.2), nod };
}
